import {
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MatrixNode,
  matrixConfigurationApi,
} from '@texas/api/endpoints/matrixConfigurationApi';
import { Role } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { useActiveSession } from '@texas/hooks/useSession';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { MatrixNodeModal } from './MatrixNodeModal';

export function MatrixNodes() {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const canEdit = hasRole(currentUser.role, [Role.SystemAdministrator]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<MatrixNode | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, refetch, loading, error } = useApiResource(
    matrixConfigurationApi.getNodes,
  );

  useEffect(() => {
    refetch(currentUser.organizationId);
  }, [currentUser.organizationId, refetch]);

  const nodes = useMemo(() => {
    const normalized = filter.trim().toLowerCase();
    return (data ?? [])
      .filter((node) => !node.archived)
      .filter((node) => node.name.toLowerCase().includes(normalized))
      .sort((left, right) => left.displayOrder - right.displayOrder);
  }, [data, filter]);

  return (
    <ConfigurationPage
      title={t('configuration.navigation.matrixNodes')}
      toolbar={
        <SearchFilterInput
          value={filter}
          style="entityDirectory"
          onChange={setFilter}
        />
      }
    >
      {selected && (
        <MatrixNodeModal
          canEdit={canEdit}
          isOpen={isOpen}
          node={selected}
          onClose={onClose}
          onSaved={() => refetch(currentUser.organizationId)}
        />
      )}
      <Flex direction="column" gap="4">
        <ErrorDetails error={error} />
        <Table variant="entity-directory">
          <Thead>
            <Tr>
              <Th>{t('general.name')}</Th>
              <Th>{t('branch.displayOrder')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!loading && nodes.length === 0 && (
              <Tr>
                <Td colSpan={2}>{t('general.noData')}</Td>
              </Tr>
            )}
            {nodes.map((node) => (
              <Tr
                key={node.id}
                cursor="pointer"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSelected(node);
                  onOpen();
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  setSelected(node);
                  onOpen();
                }}
              >
                <Td>{node.name}</Td>
                <Td>{node.displayOrder}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </ConfigurationPage>
  );
}
